import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Modal, Spinner, Alert, Badge, Button, ButtonGroup } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { ppeAPI, faceImageAPI } from '../utils/api';
import type { PPEViolation, PPERow } from '../types/ppe';
import { formatDateTimeInCompanyTimezone } from '../utils/timezone';
import { resolvePpeImagePathSegments } from '../utils/ppeImagePath';

interface ClipFrame {
  url: string;
  logTime: string;
  isViolationFrame: boolean;
}

interface ViolationClipModalProps {
  show: boolean;
  onHide: () => void;
  violation: PPEViolation | null;
  companyId: string;
  companyTimezone: string;
}

const BASE_INTERVAL_MS = 600;
const TIME_WINDOW_MS = 300_000; // ±5 minutes
const VIOLATION_PROXIMITY_MS = 3_000;

const ViolationClipModal: React.FC<ViolationClipModalProps> = ({
  show,
  onHide,
  violation,
  companyId,
  companyTimezone,
}) => {
  const { t } = useTranslation('components', { keyPrefix: 'violationClipModal' });
  const [loading, setLoading] = useState(false);
  const [frames, setFrames] = useState<ClipFrame[]>([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState<number>(1);
  const [error, setError] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const violationIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (!show) {
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
      setFrames([]);
      setCurrentIdx(0);
      setPlaying(false);
      setError(null);
      setSpeed(1);
      violationIdRef.current = null;
    }
  }, [show]);

  useEffect(() => {
    if (show && violation && companyId && violation.id !== violationIdRef.current) {
      violationIdRef.current = violation.id;
      loadFrames();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, violation?.id, companyId]);

  useEffect(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (!playing || frames.length <= 1) return;

    timerRef.current = setInterval(() => {
      setCurrentIdx(prev => {
        if (prev >= frames.length - 1) {
          setPlaying(false);
          return prev;
        }
        return prev + 1;
      });
    }, BASE_INTERVAL_MS / speed);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [playing, speed, frames.length]);

  useEffect(() => {
    frames.forEach(f => {
      const img = new Image();
      img.src = f.url;
    });
  }, [frames]);

  const loadFrames = useCallback(async () => {
    if (!violation) return;
    setLoading(true);
    setError(null);
    setFrames([]);
    setCurrentIdx(0);
    setPlaying(false);

    try {
      const violationDate = violation.log_time.slice(0, 10);

      const allRows: PPERow[] = [];
      let page = 1;
      let lastPage = 1;
      do {
        const response = await ppeAPI.getPPEDetections(companyId, {
          employee_id: violation.employee_id,
          date_from: violationDate,
          date_to: violationDate,
          per_page: 200,
          page,
        });
        allRows.push(...response.data);
        lastPage = response.meta.last_page;
        page++;
      } while (page <= lastPage);

      const violationMs = new Date(violation.log_time).getTime();

      const filtered = allRows
        .filter(row => {
          if (row.camera_number !== violation.camera_number && row.camera_name !== violation.camera_name) {
            return false;
          }
          const rowMs = new Date(row.log_time).getTime();
          return Math.abs(rowMs - violationMs) <= TIME_WINDOW_MS;
        })
        .sort((a, b) => new Date(a.log_time).getTime() - new Date(b.log_time).getTime())
        .map(row => {
          const segments = resolvePpeImagePathSegments({
            camera_number: row.camera_number,
            camera_name: row.camera_name,
            image_id: row.image_id,
            image_file: row.image_file,
          });
          const url = segments
            ? faceImageAPI.getImageUrl(companyId, segments.cameraSegment, segments.fileSegment)
            : '';
          return {
            url,
            logTime: row.log_time,
            isViolationFrame:
              Math.abs(new Date(row.log_time).getTime() - violationMs) <= VIOLATION_PROXIMITY_MS,
          };
        })
        .filter(f => f.url !== '');

      setFrames(filtered);
      if (filtered.length > 1) setPlaying(true);
    } catch (err: any) {
      setError(err.message || t('failedLoadFrames'));
    } finally {
      setLoading(false);
    }
  }, [violation, companyId, t]);

  const togglePlay = () => {
    if (frames.length <= 1) return;
    if (currentIdx >= frames.length - 1) {
      setCurrentIdx(0);
      setPlaying(true);
    } else {
      setPlaying(p => !p);
    }
  };

  const frame = frames[currentIdx] ?? null;
  const showControls = frames.length > 1;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered dialogClassName="an-media-modal" contentClassName="an-modal-panel">
      <Modal.Header closeButton className="an-section-divider">
        <Modal.Title className="d-flex align-items-center gap-2">
          <i className="bi bi-camera-video-fill text-primary" />
          <span>{t('title')}</span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="an-enterprise-page">
        {violation && (
          <div className="d-flex flex-wrap gap-2 mb-3">
            <Badge bg="dark">
              {violation.employee_name} ({violation.emp_id})
            </Badge>
            <Badge bg="secondary">{violation.camera_name}</Badge>
            <Badge bg="info">
              {formatDateTimeInCompanyTimezone(violation.log_time, companyTimezone)}
            </Badge>
            <Badge bg="danger">
              {violation.violation_type} &mdash; {violation.violation_category}
            </Badge>
          </div>
        )}

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2 text-muted">{t('loadingFrames')}</p>
          </div>
        ) : error ? (
          <Alert variant="danger">{error}</Alert>
        ) : frames.length === 0 ? (
          <Alert variant="info">
            {t('noFrames')}
          </Alert>
        ) : (
          <>
            {/* Image display */}
            <div
              className="position-relative an-video-frame mb-3 d-flex justify-content-center align-items-center"
              style={{ minHeight: 350, maxHeight: '60vh' }}
            >
              {frame && (
                <>
                  <img
                    src={frame.url}
                    alt={t('frameAlt', { index: currentIdx + 1 })}
                    style={{ maxWidth: '100%', maxHeight: '60vh', objectFit: 'contain' }}
                  />
                  {frame.isViolationFrame && (
                    <Badge
                      bg="danger"
                      className="position-absolute top-0 end-0 m-2"
                      style={{ fontSize: '0.75rem' }}
                    >
                      {t('violation')}
                    </Badge>
                  )}
                  <div
                    className="position-absolute bottom-0 start-0 m-2 px-2 py-1 rounded"
                    style={{
                      backgroundColor: 'rgb(var(--an-text-primary-rgb) / 0.65)',
                      color: 'var(--an-white)',
                      fontSize: '0.75rem',
                    }}
                  >
                    {formatDateTimeInCompanyTimezone(frame.logTime, companyTimezone)}
                  </div>
                  <div
                    className="position-absolute bottom-0 end-0 m-2 px-2 py-1 rounded"
                    style={{
                      backgroundColor: 'rgb(var(--an-text-primary-rgb) / 0.65)',
                      color: 'var(--an-white)',
                      fontSize: '0.75rem',
                    }}
                  >
                    {currentIdx + 1} / {frames.length}
                  </div>
                </>
              )}
            </div>

            {showControls && (
              <>
                {/* Seek bar */}
                <input
                  type="range"
                  className="form-range mb-2"
                  min={0}
                  max={frames.length - 1}
                  value={currentIdx}
                  onChange={e => {
                    setPlaying(false);
                    setCurrentIdx(Number(e.target.value));
                  }}
                />

                {/* Playback controls */}
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <ButtonGroup size="sm">
                    <Button
                      variant="outline-secondary"
                      onClick={() => {
                        setPlaying(false);
                        setCurrentIdx(0);
                      }}
                      title={t('firstFrame')}
                    >
                      <i className="bi bi-skip-start-fill" />
                    </Button>
                    <Button
                      variant="outline-secondary"
                      onClick={() => {
                        setPlaying(false);
                        setCurrentIdx(prev => Math.max(0, prev - 1));
                      }}
                      title={t('previousFrame')}
                    >
                      <i className="bi bi-skip-backward-fill" />
                    </Button>
                    <Button variant="primary" onClick={togglePlay} title={playing ? t('pause') : t('play')}>
                      <i className={`bi ${playing ? 'bi-pause-fill' : 'bi-play-fill'}`} />
                    </Button>
                    <Button
                      variant="outline-secondary"
                      onClick={() => {
                        setPlaying(false);
                        setCurrentIdx(prev => Math.min(frames.length - 1, prev + 1));
                      }}
                      title={t('nextFrame')}
                    >
                      <i className="bi bi-skip-forward-fill" />
                    </Button>
                    <Button
                      variant="outline-secondary"
                      onClick={() => {
                        setPlaying(false);
                        setCurrentIdx(frames.length - 1);
                      }}
                      title={t('lastFrame')}
                    >
                      <i className="bi bi-skip-end-fill" />
                    </Button>
                  </ButtonGroup>

                  <ButtonGroup size="sm">
                    {[0.5, 1, 2, 4].map(s => (
                      <Button
                        key={s}
                        variant={speed === s ? 'primary' : 'outline-secondary'}
                        onClick={() => setSpeed(s)}
                      >
                        {s}x
                      </Button>
                    ))}
                  </ButtonGroup>
                </div>
              </>
            )}
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ViolationClipModal;
